
import React from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, CreditCard, Users, TrendingUp, AlertTriangle, Activity, Database } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type CompanyWithSubscription = {
  id: number;
  razao_social: string;
  nome_fantasia: string | null;
  created_at: string;
  company_subscriptions?: Array<{
    id: number;
    status: string;
    started_at: string;
    expires_at: string | null;
    subscription_plans?: {
      name: string;
      price: number;
      max_users: number | null;
      max_services: number | null;
      max_appointments: number | null;
    } | null;
  }> | null;
};

type CompanyRiskRow = {
  companyId: number;
  companyName: string;
  planName: string;
  riskScore: number;
  riskLevel: "critical" | "warning" | "ok";
  usersTotal: number;
  maxUsers: number | null;
  servicesTotal: number;
  maxServices: number | null;
  appointmentsMonth: number;
  maxAppointments: number | null;
  storageTotalBytes: number;
  deletesLast24h: number;
  lastAtendimentoAt: string | null;
  inactiveDays: number | null;
};

type GlobalAlert = {
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
};

const STORAGE_WARNING_BYTES = 1 * 1024 * 1024 * 1024;
const STORAGE_CRITICAL_BYTES = 2 * 1024 * 1024 * 1024;

const formatBytes = (bytes: number) => {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);

const formatDateTime = (value: string | null) => {
  if (!value) return "Sem registros";
  return new Date(value).toLocaleString("pt-BR");
};

const buildCountByCompany = (
  rows: Array<{ empresa_id: number | null | undefined }> | null,
) => {
  const counts = new Map<number, number>();

  (rows || []).forEach((row) => {
    const empresaId = Number(row.empresa_id);
    if (!empresaId) return;

    counts.set(empresaId, (counts.get(empresaId) || 0) + 1);
  });

  return counts;
};

export default function AdminDashboard() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-monitoring-phase-3"],
    queryFn: async () => {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const [companiesRes, usersRes, servicesRes, appointmentsRes, deletesRes, lastAtendimentoRes, plansRes] =
        await Promise.all([
          supabase.from("empresas").select(`
            id,
            razao_social,
            nome_fantasia,
            created_at,
            company_subscriptions(
              id,
              status,
              started_at,
              expires_at,
              subscription_plans(
                name,
                price,
                max_users,
                max_services,
                max_appointments
              )
            )
          `),
          supabase.from("users").select("empresa_id"),
          supabase.from("servicos").select("empresa_id"),
          supabase
            .from("atendimentos")
            .select("empresa_id")
            .gte("created_at", monthStart.toISOString()),
          supabase
            .from("audit_log")
            .select("empresa_id")
            .eq("operation", "DELETE")
            .gte("created_at", last24h),
          supabase
            .from("atendimentos")
            .select("empresa_id, created_at")
            .order("created_at", { ascending: false }),
          supabase
            .from("subscription_plans")
            .select("id", { count: "exact", head: true })
            .eq("is_active", true),
        ]);

      if (companiesRes.error) throw companiesRes.error;
      if (usersRes.error) throw usersRes.error;
      if (servicesRes.error) throw servicesRes.error;
      if (appointmentsRes.error) throw appointmentsRes.error;
      if (deletesRes.error) throw deletesRes.error;
      if (lastAtendimentoRes.error) throw lastAtendimentoRes.error;
      if (plansRes.error) throw plansRes.error;

      const companies = (companiesRes.data || []) as CompanyWithSubscription[];
      const usersByCompany = buildCountByCompany(usersRes.data);
      const servicesByCompany = buildCountByCompany(servicesRes.data);
      const appointmentsByCompany = buildCountByCompany(appointmentsRes.data);
      const deletesByCompany = buildCountByCompany(deletesRes.data);

      const lastAtendimentoByCompany = new Map<number, string>();
      (lastAtendimentoRes.data || []).forEach((row) => {
        const empresaId = Number(row.empresa_id);
        if (!empresaId || lastAtendimentoByCompany.has(empresaId)) return;
        lastAtendimentoByCompany.set(empresaId, row.created_at);
      });

      const supabaseRpc = supabase as unknown as {
        rpc: (
          fn: string,
          args?: Record<string, unknown>,
        ) => Promise<{
          data:
            | Array<{ bucket_id: string; files_count: number; total_bytes: number }>
            | null;
          error: unknown;
        }>;
      };

      const storageEntries = await Promise.all(
        companies.map(async (company) => {
          const { data: storageData, error: storageError } = await supabaseRpc.rpc(
            "admin_company_storage_usage",
            {
              p_empresa_id: company.id,
            },
          );

          if (storageError) {
            console.error("Erro ao buscar storage da empresa:", company.id, storageError);
            return [company.id, 0] as const;
          }

          const totalBytes = (storageData || []).reduce(
            (sum, bucket) => sum + Number(bucket.total_bytes || 0),
            0,
          );

          return [company.id, totalBytes] as const;
        }),
      );

      const storageByCompany = new Map<number, number>(storageEntries);

      const riskRows: CompanyRiskRow[] = companies.map((company) => {
        const activeSubscription = company.company_subscriptions?.find(
          (subscription) => subscription.status === "active",
        );

        const plan = activeSubscription?.subscription_plans;
        const maxUsers = plan?.max_users ?? null;
        const maxServices = plan?.max_services ?? null;
        const maxAppointments = plan?.max_appointments ?? null;

        const usersTotal = usersByCompany.get(company.id) || 0;
        const servicesTotal = servicesByCompany.get(company.id) || 0;
        const appointmentsMonth = appointmentsByCompany.get(company.id) || 0;
        const deletesLast24h = deletesByCompany.get(company.id) || 0;
        const storageTotalBytes = storageByCompany.get(company.id) || 0;
        const lastAtendimentoAt = lastAtendimentoByCompany.get(company.id) || null;

        const inactiveDays = lastAtendimentoAt
          ? Math.floor((Date.now() - new Date(lastAtendimentoAt).getTime()) / (1000 * 60 * 60 * 24))
          : null;

        let riskScore = 0;

        if (maxUsers && usersTotal > maxUsers) {
          riskScore += 50;
        } else if (maxUsers && usersTotal / maxUsers >= 0.8) {
          riskScore += 15;
        }

        if (maxServices && servicesTotal > maxServices) {
          riskScore += 35;
        } else if (maxServices && servicesTotal / maxServices >= 0.8) {
          riskScore += 15;
        }

        if (maxAppointments && appointmentsMonth / maxAppointments >= 1) {
          riskScore += 20;
        } else if (maxAppointments && appointmentsMonth / maxAppointments >= 0.8) {
          riskScore += 10;
        }

        if (storageTotalBytes >= STORAGE_CRITICAL_BYTES) {
          riskScore += 25;
        } else if (storageTotalBytes >= STORAGE_WARNING_BYTES) {
          riskScore += 10;
        }

        if (deletesLast24h >= 50) {
          riskScore += 20;
        } else if (deletesLast24h >= 20) {
          riskScore += 10;
        }

        if (activeSubscription && inactiveDays !== null && inactiveDays >= 30) {
          riskScore += 10;
        }

        const riskLevel: CompanyRiskRow["riskLevel"] =
          riskScore >= 60 ? "critical" : riskScore >= 25 ? "warning" : "ok";

        return {
          companyId: company.id,
          companyName: company.nome_fantasia || company.razao_social,
          planName: plan?.name || "Sem plano",
          riskScore,
          riskLevel,
          usersTotal,
          maxUsers,
          servicesTotal,
          maxServices,
          appointmentsMonth,
          maxAppointments,
          storageTotalBytes,
          deletesLast24h,
          lastAtendimentoAt,
          inactiveDays,
        };
      });

      const riskRanking = [...riskRows]
        .sort((a, b) => b.riskScore - a.riskScore)
        .slice(0, 12);

      const criticalCount = riskRows.filter((row) => row.riskLevel === "critical").length;
      const warningCount = riskRows.filter((row) => row.riskLevel === "warning").length;
      const totalDeletesLast24h = riskRows.reduce((sum, row) => sum + row.deletesLast24h, 0);
      const totalStorageBytes = riskRows.reduce((sum, row) => sum + row.storageTotalBytes, 0);

      const activeSubscriptions = companies.filter((company) =>
        company.company_subscriptions?.some((subscription) => subscription.status === "active"),
      );

      const totalRevenue = activeSubscriptions.reduce((sum, company) => {
        const activeSubscription = company.company_subscriptions?.find(
          (subscription) => subscription.status === "active",
        );
        return sum + Number(activeSubscription?.subscription_plans?.price || 0);
      }, 0);

      const deleteSpikeCompanies = riskRows.filter((row) => row.deletesLast24h >= 20).length;
      const inactiveCompanies = riskRows.filter(
        (row) => row.inactiveDays !== null && row.inactiveDays >= 30,
      ).length;

      const globalAlerts: GlobalAlert[] = [];

      if (criticalCount > 0) {
        globalAlerts.push({
          severity: "critical",
          title: "Empresas em estado crítico",
          description: `${criticalCount} empresa(s) com risco elevado por extrapolação de limites ou sinais de anomalia.`,
        });
      }

      if (warningCount > 0) {
        globalAlerts.push({
          severity: "warning",
          title: "Empresas em atenção",
          description: `${warningCount} empresa(s) com consumo acima de 80% em pelo menos um indicador monitorado.`,
        });
      }

      if (deleteSpikeCompanies > 0) {
        globalAlerts.push({
          severity: "warning",
          title: "Pico de exclusões",
          description: `${deleteSpikeCompanies} empresa(s) registraram volume alto de DELETE nas últimas 24h.`,
        });
      }

      if (inactiveCompanies > 0) {
        globalAlerts.push({
          severity: "info",
          title: "Risco de churn operacional",
          description: `${inactiveCompanies} empresa(s) com assinatura ativa sem atividade recente de atendimento.`,
        });
      }

      if (globalAlerts.length === 0) {
        globalAlerts.push({
          severity: "info",
          title: "Operação estável",
          description: "Nenhum alerta crítico identificado na janela atual de monitoramento.",
        });
      }

      return {
        summary: {
          totalCompanies: companies.length,
          activeSubscriptions: activeSubscriptions.length,
          totalPlans: plansRes.count || 0,
          totalRevenue,
          criticalCount,
          warningCount,
          totalStorageBytes,
          totalDeletesLast24h,
        },
        recentCompanies: [...companies]
          .sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          )
          .slice(0, 6),
        globalAlerts,
        riskRanking,
      };
    },
  });

  const getRiskBadgeClass = (riskLevel: CompanyRiskRow["riskLevel"]) => {
    if (riskLevel === "critical") return "bg-red-100 text-red-800";
    if (riskLevel === "warning") return "bg-amber-100 text-amber-800";
    return "bg-emerald-100 text-emerald-800";
  };

  const getRiskLabel = (riskLevel: CompanyRiskRow["riskLevel"]) => {
    if (riskLevel === "critical") return "Crítico";
    if (riskLevel === "warning") return "Atenção";
    return "OK";
  };

  const getAlertBadgeClass = (severity: GlobalAlert["severity"]) => {
    if (severity === "critical") return "bg-red-100 text-red-800";
    if (severity === "warning") return "bg-amber-100 text-amber-800";
    return "bg-blue-100 text-blue-800";
  };

  const statCards = [
    {
      title: "Total de Empresas",
      value: data?.summary.totalCompanies || 0,
      icon: Building2,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: "Assinaturas Ativas",
      value: data?.summary.activeSubscriptions || 0,
      icon: Users,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      title: "Planos Disponíveis",
      value: data?.summary.totalPlans || 0,
      icon: CreditCard,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      title: "Receita Mensal",
      value: formatCurrency(data?.summary.totalRevenue || 0),
      icon: TrendingUp,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      title: "Alertas Críticos",
      value: data?.summary.criticalCount || 0,
      icon: AlertTriangle,
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
    {
      title: "DELETE em 24h",
      value: data?.summary.totalDeletesLast24h || 0,
      icon: Activity,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      title: "Storage Global",
      value: formatBytes(data?.summary.totalStorageBytes || 0),
      icon: Database,
      color: "text-cyan-600",
      bgColor: "bg-cyan-50",
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Dashboard Executivo"
          subtitle="Central de alertas globais e ranking de risco das empresas (Fase 3)."
          backTo="/admin/control-center"
        />

        {isError && (
          <Card className="rounded-2xl border-red-200 bg-red-50">
            <CardContent className="pt-6 flex items-center justify-between gap-4">
              <p className="text-sm text-red-700">
                Não foi possível carregar as métricas executivas no momento.
              </p>
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="rounded-2xl border-border/60 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-700">
                    {stat.title}
                  </CardTitle>
                  <div className={`w-9 h-9 rounded-xl ${stat.bgColor} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900 tabular-nums">
                    {isLoading ? "..." : stat.value}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Global Alerts + Recent Companies */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle>Central de Alertas Globais</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-sm text-gray-500">Carregando alertas...</p>
              ) : (
                <div className="space-y-3">
                  {(data?.globalAlerts || []).map((alert, index) => (
                    <div key={`${alert.title}-${index}`} className="rounded-xl border border-border/60 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold">{alert.title}</p>
                        <Badge className={getAlertBadgeClass(alert.severity)}>
                          {alert.severity === "critical"
                            ? "Crítico"
                            : alert.severity === "warning"
                              ? "Atenção"
                              : "Info"}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle>Empresas Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-sm text-gray-500">Carregando empresas...</p>
              ) : (
                <div className="space-y-3">
                  {(data?.recentCompanies || []).map((company) => (
                    <div key={company.id} className="flex items-center justify-between rounded-xl border border-border/60 p-3">
                      <div>
                        <p className="text-sm font-semibold">
                          {company.nome_fantasia || company.razao_social}
                        </p>
                        <p className="text-xs text-gray-500">
                          Cadastro: {new Date(company.created_at).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                      <Button asChild variant="outline" size="sm">
                        <Link to="/admin/companies">Ver empresa</Link>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Risk ranking */}
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle>Ranking de Risco por Empresa</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-gray-500">Carregando ranking...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Nível</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Assentos</TableHead>
                    <TableHead>Serviços</TableHead>
                    <TableHead>Atend. mês</TableHead>
                    <TableHead>Storage</TableHead>
                    <TableHead>DELETE 24h</TableHead>
                    <TableHead>Último atendimento</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.riskRanking || []).map((row) => (
                    <TableRow key={row.companyId}>
                      <TableCell className="font-medium">{row.companyName}</TableCell>
                      <TableCell>{row.planName}</TableCell>
                      <TableCell>
                        <Badge className={getRiskBadgeClass(row.riskLevel)}>
                          {getRiskLabel(row.riskLevel)}
                        </Badge>
                      </TableCell>
                      <TableCell>{row.riskScore}</TableCell>
                      <TableCell>
                        {row.usersTotal}/{row.maxUsers ?? "∞"}
                      </TableCell>
                      <TableCell>
                        {row.servicesTotal}/{row.maxServices ?? "∞"}
                      </TableCell>
                      <TableCell>
                        {row.appointmentsMonth}/{row.maxAppointments ?? "∞"}
                      </TableCell>
                      <TableCell>{formatBytes(row.storageTotalBytes)}</TableCell>
                      <TableCell>{row.deletesLast24h}</TableCell>
                      <TableCell>{formatDateTime(row.lastAtendimentoAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
